import { DEGEN_MIN_LEVERAGE } from '@ui/constants/uiConfig';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { Badge, BadgeProps } from '@ui/lib/park/ui/badge';
import { Text } from '@ui/lib/park/ui/text';
import { Tooltip } from '@ui/lib/park/ui/tooltip';
import { Nullable } from '@ui/types/utility';
import BigNumber from 'bignumber.js';
import { css } from 'styled-system/css';

import { GenericTradeParams } from '../types';
import { formatLeverage } from './TradeModule/utils/formatLeverage';

const isDegenMode = (leverage: Nullable<BigNumber>, isDegen?: boolean) => {
  if (isDegen !== undefined) return isDegen; // if passed in isDegen, use it directly

  return leverage?.isGreaterThanOrEqualTo(DEGEN_MIN_LEVERAGE); // if not passed in, determine based on leverage value
};

export const LeverageBadge = ({
  leverage,
  isDegen,
  ...p
}: BadgeProps & {
  leverage: Nullable<BigNumber>;
  isDegen?: boolean;
}) => {
  return (
    <Tooltip.Root
      lazyMount
      unmountOnExit
      positioning={{ placement: 'top' }}
      openDelay={100}
      closeDelay={0}
      variant="darkBox"
    >
      <Tooltip.Trigger asChild>
        <Badge
          variant="leverage"
          size="sm"
          textStyle="text.xxsmall"
          fontWeight="600"
          color="greyWhite.lightGrey1"
          cursor="help"
          {...p}
          data-cy="LeverageBadge"
          data-cy-value={leverage ? leverage?.toNumber() : '--'}
        >
          {leverage ? formatLeverage(leverage.toNumber()) : '--'}x
        </Badge>
      </Tooltip.Trigger>
      <Tooltip.Positioner>
        <Tooltip.Content maxW="xs">
          <Tooltip.Arrow>
            <Tooltip.ArrowTip />
          </Tooltip.Arrow>
          <Text textStyle="text.small" color="white">
            Effective Leverage = Position Value / Margin Value
          </Text>
        </Tooltip.Content>
      </Tooltip.Positioner>
    </Tooltip.Root>
  );
};

export const TradeLeverageBadge = ({
  genericTradeParams,
}: GenericTradeParams) => {
  const collateralMarketPrice = usePayTokenPriceLatest(
    genericTradeParams.collateralToken,
  );
  const pairMarketPrice = usePriceLive(genericTradeParams.pair.baseSymbol);

  if (!collateralMarketPrice || !pairMarketPrice) return <></>;

  return (
    <LeverageBadge
      leverage={genericTradeParams.getLeverage({
        collateralTokenMarketPrice: collateralMarketPrice,
      })}
      isDegen={genericTradeParams.pair.isDegenPair}
    />
  );
};
